import Box from '@amzn/awsui-components-core/box';
import { AgGridReact } from 'ag-grid-react';
import React from 'react';
import { ReportGridToolbar } from 'src/components/star-grid/components/ReportGridToolbar';
import { ReportHierarchyHeader, ReportPeriodHeader } from 'src/components/star-grid/components/ReportGridHeaders';
import { usePnlReportGrid } from 'src/components/star-grid/hooks/usePnlReportGrid';
import { PnlGridRow, PnlPivotModel } from 'src/components/star-grid/starGridTypes';
import { pnlDefaultColDef } from 'src/components/star-grid/utils/pnlGrid';
import './styles/pnl-report-grid.scss';

export interface PnlReportGridProps {
  readonly reportName?: string;
  readonly reportFamily?: string;
  readonly model: PnlPivotModel;
  /** True while a (re-)query is in flight — shows the grid's loading overlay. */
  readonly loading: boolean;
  /** Selected display-scale id (drives client-side value formatting via grid context). */
  readonly scaleId: string;
  /** Dimensions currently in the row hierarchy — used to filter the drill menu. */
  readonly groupBy: readonly string[];
  /** Dimensions the report can drill into (not already grouped). */
  readonly drillableDimensions: readonly string[];
  /** Called when the user picks a drill dimension from the context menu. */
  readonly onDrillDown: (dimension: string) => void;
  /** Optional field → human label map for the row-hierarchy header + drill menu. */
  readonly dimensionLabels?: Record<string, string>;
}

const GRID_COMPONENTS = { pnlPeriodHeader: ReportPeriodHeader, pnlHierarchyHeader: ReportHierarchyHeader };
const GRID_SIDEBAR = { hiddenByDefault: true, toolPanels: ['columns'] };

const PnlReportGrid: React.FC<PnlReportGridProps> = (props) => {
  const state = usePnlReportGrid(props);
  return (
    <section className="sg-report-table" aria-label={props.reportName}>
      <ReportGridToolbar state={state} />
      {props.model.rows.length === 0 ? (
        <Box padding="l" textAlign="center">
          {state.text('noReportRows')}
        </Box>
      ) : (
        <div className="sg-pnl-grid-wrapper" ref={state.viewportRef}>
          <div className="sg-pnl-grid">
            <AgGridReact<PnlGridRow>
              ref={state.gridRef}
              rowData={props.model.rows}
              columnDefs={state.columnDefs}
              defaultColDef={pnlDefaultColDef}
              loading={props.loading}
              treeData={!state.statement}
              getDataPath={state.statement ? undefined : state.getDataPath}
              autoGroupColumnDef={state.statement ? undefined : state.autoGroupColumnDef}
              groupDefaultExpanded={1}
              aggFuncs={state.aggFuncs}
              getRowClass={state.getRowClass}
              suppressAggFuncInHeader
              context={{ scaleId: props.scaleId }}
              theme={state.theme}
              sideBar={GRID_SIDEBAR}
              components={GRID_COMPONENTS}
              cellSelection
              rowBuffer={20}
              rowHeight={state.rowHeight}
              getRowHeight={state.getRowHeight}
              headerHeight={50}
              groupHeaderHeight={29}
              quickFilterText={state.search}
              getContextMenuItems={state.getContextMenuItems}
              onRowDataUpdated={state.restoreExpandedState}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default PnlReportGrid;
